import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { APP_NAME } from '../../constants/brand'
import { api, type ApiKeyListItem } from '../../api/client'
import { createNadDebug } from '../../utils/debugLog'
import {
  forgetAll,
  forgetKey,
  getRememberedKeys,
  pruneRemembered,
  rememberKey,
} from '../../utils/adminApiKeyStore'
import { trackInteraction } from '../../analytics/interactionAnalytics'

// NAD trace for the API-key management surface (feature=admin-api-keys).
// NEVER pass a raw `dwk_` value into this logger — only ids and masked prefixes.
const apiKeysLog = createNadDebug('dw:admin-api-keys:ui')

// Keep the provider's actual reason instead of collapsing every failure into
// one generic string (CLAUDE.md #7 — surface the specific reason so the caller
// can act on it). Narrowed, no `unknown`/casts.
function withReason(message: string, reason: string): string {
  return reason ? `${message}: ${reason}` : message
}

/** Narrow a caught value to a displayable reason without the `unknown` keyword. */
function reasonOf(err: Error | string | null): string {
  if (err instanceof Error) return err.message
  return typeof err === 'string' ? err : ''
}

interface ApiKeysSectionProps {
  /**
   * When true, the raw key returned by `POST /auth/api-keys` is cached in this
   * browser (see `utils/adminApiKeyStore`) and re-displayed on every later
   * visit, plus forget/revoke-all controls are shown. Default `false` keeps the
   * historical show-once behavior for the account-settings surface.
   */
  persistentReveal?: boolean
}

/**
 * Per-user API keys (server-managed). Extracted verbatim from `SettingsForm` so
 * the admin page can mount it with `persistentReveal`; `SettingsForm` renders it
 * with the default (show-once) behavior it has always had.
 *
 * Renders nothing without a session. That gate is load-bearing: the `/admin/api`
 * route has a local-dev bypass, and a session-less caller resolves server-side to
 * the non-UUID `debug-user`, which would run `WHERE user_id = 'debug-user'`
 * against a `UUID NOT NULL` column and 500. Callers that need a visible empty
 * state (the admin page) render their own signed-out copy.
 */
export default function ApiKeysSection({ persistentReveal = false }: ApiKeysSectionProps) {
  const { t, i18n } = useTranslation('settings')
  const { session } = useAuth()
  const userId = session?.user?.id ?? ''
  const isSignedIn = Boolean(userId)
  // Mirrors `userId` for async guards: closures capture the value at call time,
  // so comparing against a ref is what detects an account switch mid-request.
  const userIdRef = useRef(userId)
  useEffect(() => {
    userIdRef.current = userId
  }, [userId])

  const [userApiKeys, setUserApiKeys] = useState<ApiKeyListItem[]>([])
  const [newKeyName, setNewKeyName] = useState('')
  const [apiKeysLoading, setApiKeysLoading] = useState(false)
  const [apiKeysError, setApiKeysError] = useState<string | null>(null)
  // Reveal of a just-created key. Without `persistentReveal` this is the ONLY
  // copy — cleared on dismiss, logout, or when that key is revoked.
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<{ id: string; key: string } | null>(null)
  const [copiedKeyId, setCopiedKeyId] = useState<string | null>(null)
  // Raw keys this browser remembers, by key id. Always empty without `persistentReveal`.
  const [rememberedKeys, setRememberedKeys] = useState<Record<string, string>>({})
  const [confirmingRevokeAll, setConfirmingRevokeAll] = useState(false)

  const loadApiKeys = useCallback(async () => {
    setApiKeysLoading(true)
    setApiKeysError(null)
    // Bind this request to the account that started it. Without the guard, a
    // list begun as user A can resolve after a switch to user B and paint A's
    // key metadata into B's session.
    const requestedFor = userId
    try {
      const keys = await api.listApiKeys()
      if (requestedFor !== userIdRef.current) return
      setUserApiKeys(keys)
      let rememberedCount = 0
      let prunedCount = 0
      if (persistentReveal && userId) {
        // Self-clean: a key revoked from another device disappears from the
        // listing, so its cached secret must go too.
        const before = Object.keys(getRememberedKeys(userId)).length
        const survivors = pruneRemembered(userId, keys.map((k) => k.id))
        setRememberedKeys(survivors)
        rememberedCount = Object.keys(survivors).length
        prunedCount = before - rememberedCount
      }
      // `remembered_count` is what makes the persistence claim provable from the
      // trace alone: after a reload it must still be >= 1 for a key this browser
      // minted. Counts only — never a raw key.
      apiKeysLog({
        event: 'admin-api-keys:list_loaded',
        feature: 'admin-api-keys',
        concern: 'ui',
        count: keys.length,
        persistent_reveal: persistentReveal,
        remembered_count: rememberedCount,
        pruned_count: prunedCount,
      })
    } catch (err) {
      if (requestedFor !== userIdRef.current) return
      setApiKeysError(withReason(t('settingsForm.failedToLoadApiKeys'), reasonOf(err instanceof Error ? err : null)))
      apiKeysLog({
        event: 'admin-api-keys:list_failed',
        feature: 'admin-api-keys',
        concern: 'ui',
      })
    } finally {
      if (requestedFor === userIdRef.current) setApiKeysLoading(false)
    }
  }, [t, persistentReveal, userId])

  // Load keys when logged in. Clear the in-memory reveal on session change so a
  // stale secret can never linger across accounts.
  // Keyed on the ACCOUNT, not the session object. `AuthContext` calls
  // setSession() on every onAuthStateChange — including TOKEN_REFRESHED, which
  // changes object identity without changing account. Depending on `session`
  // here wiped the one-time reveal on a silent token refresh, losing the only
  // copy of a key the user had just minted.
  useEffect(() => {
    setNewlyCreatedKey(null)
    setConfirmingRevokeAll(false)
    if (isSignedIn) {
      if (persistentReveal && userId) {
        // Cross-account scoping is NOT done here — AuthContext drops foreign
        // buckets on the sign-in boundary, so it also happens for an admin who
        // never opens this page. This only reads what survived.
        setRememberedKeys(getRememberedKeys(userId))
      }
      loadApiKeys()
    } else {
      setUserApiKeys([])
      setRememberedKeys({})
    }
  }, [isSignedIn, loadApiKeys, persistentReveal, userId])

  const handleCreateApiKey = async () => {
    const name = newKeyName.trim()
    if (!name) return
    setApiKeysError(null)
    const requestedFor = userId
    try {
      // The raw key is returned ONLY by this POST response and is not stored
      // server-side (RFC-0822). Capture it now — with `persistentReveal` we also
      // cache it in this browser so it stays readable across reloads.
      const created = await api.createApiKey(name)
      trackInteraction('settings_api_key_action', { action: 'create', result: 'success' })
      // Never reveal a raw key minted for a previous account in a new session.
      if (requestedFor !== userIdRef.current) return
      setNewlyCreatedKey({ id: created.id, key: created.key })
      let remembered = false
      if (persistentReveal && userId) {
        // A dropped write (quota / blocked storage) must not be reported as
        // persisted — the reveal banner is then the ONLY copy of the key.
        remembered = rememberKey(userId, created.id, created.key)
        if (remembered) {
          setRememberedKeys((prev) => ({ ...prev, [created.id]: created.key }))
        } else {
          setApiKeysError(t('settingsForm.keyStoreWriteFailed'))
        }
      }
      apiKeysLog({
        event: 'admin-api-keys:created',
        feature: 'admin-api-keys',
        concern: 'ui',
        key_id: created.id,
        key_prefix: created.key_prefix,
        remembered,
      })
      setNewKeyName('')
      await loadApiKeys()
    } catch (err) {
      trackInteraction('settings_api_key_action', { action: 'create', result: 'failure' })
      setApiKeysError(withReason(t('settingsForm.failedToCreateApiKey'), reasonOf(err instanceof Error ? err : null)))
      apiKeysLog({
        event: 'admin-api-keys:create_failed',
        feature: 'admin-api-keys',
        concern: 'ui',
      })
    }
  }

  /** Revoke one key. Does NOT reload the listing — callers decide, so a
   *  revoke-all does one refresh instead of one per key. */
  const revokeOne = useCallback(
    async (keyId: string) => {
      await api.revokeApiKey(keyId)
      setNewlyCreatedKey((prev) => (prev?.id === keyId ? null : prev))
      if (persistentReveal && userId) {
        forgetKey(userId, keyId)
        setRememberedKeys((prev) => {
          const next = { ...prev }
          delete next[keyId]
          return next
        })
      }
      apiKeysLog({
        event: 'admin-api-keys:revoked',
        feature: 'admin-api-keys',
        concern: 'ui',
        key_id: keyId,
      })
    },
    [persistentReveal, userId],
  )

  const handleRevokeApiKey = async (keyId: string) => {
    setApiKeysError(null)
    try {
      await revokeOne(keyId)
      trackInteraction('settings_api_key_action', { action: 'revoke', result: 'success' })
      await loadApiKeys()
    } catch (err) {
      trackInteraction('settings_api_key_action', { action: 'revoke', result: 'failure' })
      setApiKeysError(withReason(t('settingsForm.failedToRevokeApiKey'), reasonOf(err instanceof Error ? err : null)))
      apiKeysLog({
        event: 'admin-api-keys:revoke_failed',
        feature: 'admin-api-keys',
        concern: 'ui',
        key_id: keyId,
      })
    }
  }

  const handleRevokeAll = async () => {
    setConfirmingRevokeAll(false)
    setApiKeysError(null)
    apiKeysLog({
      event: 'admin-api-keys:revoke_all',
      feature: 'admin-api-keys',
      concern: 'ui',
      count: userApiKeys.length,
    })
    // Snapshot the ids first: the list state is refreshed once at the end, so
    // iterating it directly would be reading a value we are about to replace.
    const keyIds = userApiKeys.map((k) => k.id)
    let failed = 0
    for (const keyId of keyIds) {
      try {
        await revokeOne(keyId)
      } catch {
        // Keep going — one bad key must not strand the rest on the account.
        failed += 1
        apiKeysLog({
          event: 'admin-api-keys:revoke_failed',
          feature: 'admin-api-keys',
          concern: 'ui',
          key_id: keyId,
        })
      }
    }
    await loadApiKeys()
    if (failed > 0) setApiKeysError(t('settingsForm.failedToRevokeApiKey'))
  }

  const handleForgetStored = () => {
    if (!userId) return
    if (!forgetAll(userId)) {
      // Storage still holds usable raw keys — clearing the UI here would tell
      // the user their secrets are gone when they are not.
      setApiKeysError(t('settingsForm.keyForgetFailed'))
      return
    }
    setRememberedKeys({})
    setNewlyCreatedKey(null)
    apiKeysLog({
      event: 'admin-api-keys:forgot_stored',
      feature: 'admin-api-keys',
      concern: 'ui',
    })
  }

  const handleCopyKey = async (keyId: string, keyValue: string) => {
    try {
      // Only flash "Copied!" once the write actually succeeds — a denied or
      // failed clipboard write must not falsely claim the key was copied.
      await navigator.clipboard.writeText(keyValue)
      setCopiedKeyId(keyId)
      setTimeout(() => setCopiedKeyId(null), 2000)
      apiKeysLog({
        event: 'admin-api-keys:copied',
        feature: 'admin-api-keys',
        concern: 'ui',
        key_id: keyId,
      })
    } catch {
      setApiKeysError(t('settingsForm.clipboardError'))
    }
  }

  if (!session) return null

  const storedCount = Object.keys(rememberedKeys).length

  return (
    <div data-testid="api-keys-section">
      <h3 className="text-sm font-medium text-white/80 mb-1">{t('settingsForm.apiKeys')}</h3>
      <p className="text-xs font-medium text-white/40 mb-3">
        {t('settingsForm.apiKeysDescription', { appName: APP_NAME })}
      </p>

      {/* Create form */}
      <div className="flex gap-2 mb-3">
        <input
          type="text"
          value={newKeyName}
          onChange={(e) => setNewKeyName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleCreateApiKey()}
          placeholder={t('settingsForm.keyNamePlaceholder')}
          data-testid="api-key-name-input"
          className="flex-1 px-3 py-2 bg-surface-dark rounded border border-white/10 text-sm focus:outline-none focus:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        />
        <button
          onClick={handleCreateApiKey}
          disabled={!newKeyName.trim()}
          data-testid="api-key-generate-button"
          className="px-3 py-2 bg-primary hover:bg-primary-light disabled:opacity-50 disabled:cursor-not-allowed rounded text-sm font-medium transition-colors whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {t('settingsForm.generateKey')}
        </button>
      </div>

      {apiKeysError && (
        <p className="text-xs font-medium text-red-400 mb-2" data-testid="api-keys-error">{apiKeysError}</p>
      )}

      {/* Reveal of the just-created key. The warning copy differs by mode: with
          `persistentReveal` the "won't be shown again" wording would be false. */}
      {newlyCreatedKey && (
        <div
          data-testid="api-key-reveal"
          className="mb-3 p-3 rounded-lg border border-warning/40 bg-warning/10"
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-warning">
              {persistentReveal
                ? t('settingsForm.copyKeyStoredLocally')
                : t('settingsForm.copyKeyWarning')}
            </span>
            <div className="flex items-center gap-1 ml-2">
              <button
                onClick={() => handleCopyKey(newlyCreatedKey.id, newlyCreatedKey.key)}
                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                  copiedKeyId === newlyCreatedKey.id
                    ? 'bg-green-500/20 text-green-400'
                    : 'bg-white/10 hover:bg-white/20 text-white/80'
                }`}
              >
                {copiedKeyId === newlyCreatedKey.id ? t('settingsForm.copied') : t('settingsForm.copy')}
              </button>
              <button
                onClick={() => setNewlyCreatedKey(null)}
                className="px-2 py-1 bg-white/10 hover:bg-white/20 text-white/80 rounded text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                title={t('settingsForm.dismiss')}
              >
                {t('settingsForm.dismiss')}
              </button>
            </div>
          </div>
          <code className="block text-xs font-medium text-white break-all" data-testid="api-key-reveal-value">
            {newlyCreatedKey.key}
          </code>
        </div>
      )}

      {/* Key list */}
      {apiKeysLoading ? (
        <p className="text-xs font-medium text-white/40">{t('settingsForm.loadingKeys')}</p>
      ) : userApiKeys.length === 0 ? (
        <p className="text-xs font-medium text-white/40" data-testid="api-keys-empty">{t('settingsForm.noApiKeys')}</p>
      ) : (
        <div className="space-y-2">
          {userApiKeys.map((k) => {
            // With `persistentReveal`, show the full key when this browser still
            // holds it; otherwise only the masked prefix is ever available
            // (RFC-0822 — the server never returns the raw key again).
            const remembered = persistentReveal ? rememberedKeys[k.id] : undefined
            const displayKey = remembered ?? k.key_prefix + '...'
            return (
              <div
                key={k.id}
                data-testid={`api-key-row-${k.id}`}
                className="p-2.5 rounded-lg border border-white/10 bg-surface-dark"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white truncate">{k.name}</span>
                  <div className="flex items-center gap-1 ml-2">
                    {remembered && (
                      <button
                        onClick={() => handleCopyKey(k.id, remembered)}
                        data-testid={`api-key-copy-${k.id}`}
                        className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                          copiedKeyId === k.id
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-white/10 hover:bg-white/20 text-white/80'
                        }`}
                      >
                        {copiedKeyId === k.id ? t('settingsForm.copied') : t('settingsForm.copy')}
                      </button>
                    )}
                    <button
                      onClick={() => handleRevokeApiKey(k.id)}
                      data-testid={`api-key-revoke-${k.id}`}
                      className="px-2 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded text-xs font-medium transition-colors whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      {t('settingsForm.revoke')}
                    </button>
                  </div>
                </div>
                <code
                  className={`block text-xs font-medium mt-1 break-all ${remembered ? 'text-white' : 'text-white/60'}`}
                  data-testid={`api-key-value-${k.id}`}
                >
                  {displayKey}
                </code>
                {persistentReveal && !remembered && (
                  <div className="text-xs font-medium text-white/40 mt-0.5" data-testid={`api-key-not-stored-${k.id}`}>
                    {t('settingsForm.keyNotStoredLocally')}
                  </div>
                )}
                <div className="text-xs font-medium text-white/40 mt-0.5">
                  {t('settingsForm.created', { date: new Date(k.created_at).toLocaleDateString(i18n.language) })}
                  {' · '}
                  {k.last_used_at
                    ? t('settingsForm.lastUsed', { date: new Date(k.last_used_at).toLocaleDateString(i18n.language) })
                    : t('settingsForm.neverUsed')}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Local-cache controls. Only meaningful when this browser holds secrets. */}
      {persistentReveal && (
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="api-keys-local-controls">
          <span className="text-xs font-medium text-white/40">
            {t('settingsForm.storedLocallyCount', { count: storedCount })}
          </span>
          <button
            onClick={handleForgetStored}
            disabled={storedCount === 0}
            data-testid="api-keys-forget-stored"
            className="px-2 py-1 bg-white/10 hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed text-white/80 rounded text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            {t('settingsForm.forgetStoredKeys')}
          </button>
          {confirmingRevokeAll ? (
            <>
              <span className="text-xs font-medium text-white/60">{t('settingsForm.revokeAllConfirm')}</span>
              <button
                onClick={() => setConfirmingRevokeAll(false)}
                className="px-2 py-1 text-xs font-medium text-white/60 hover:text-white transition-colors"
              >
                {t('settingsForm.revokeAllCancel')}
              </button>
              <button
                onClick={handleRevokeAll}
                data-testid="api-keys-revoke-all-confirm"
                className="px-2 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded text-xs font-medium transition-colors"
              >
                {t('settingsForm.revokeAllConfirmYes')}
              </button>
            </>
          ) : (
            <button
              onClick={() => setConfirmingRevokeAll(true)}
              disabled={userApiKeys.length === 0}
              data-testid="api-keys-revoke-all"
              className="px-2 py-1 bg-red-500/20 hover:bg-red-500/30 disabled:opacity-40 disabled:cursor-not-allowed text-red-400 rounded text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {t('settingsForm.revokeAll')}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
